var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef } from 'react';
import { useTheme } from '@heathmont/moon-themes';
import { disabled, rem } from '@heathmont/moon-utils';
import styled from 'styled-components';
import Loader from '../loader/Loader';
import IconSuccess from '../private/icons/IconSuccess';
import { oopsAnimation } from './oopsAnimation';
import { pulseAnimation } from './pulseAnimation';
import { buttonSize } from './size';
import { buttonMockStateClass, buttonHover, buttonActive } from './states';
import { buttonVariant } from './variants';
/* eslint-enable @typescript-eslint/no-explicit-any */
/**
 * Component
 */
const StyledButton = styled.button.withConfig({
    shouldForwardProp: (prop) => !['fullWidth', 'iconRight', 'iconLeft'].includes(prop),
})(({ theme: { border, fontWeight, opacity, radius, transitionDuration }, fullWidth, }) => (Object.assign({ display: 'inline-block', width: fullWidth ? '100%' : '', minHeight: rem(24), fontFamily: 'inherit', fontWeight: fontWeight.semibold, textDecoration: 'none', cursor: 'pointer', border, borderColor: 'transparent', borderRadius: rem(radius.largest), transition: `background-color ${transitionDuration.default}s`, whiteSpace: 'nowrap' }, disabled(opacity.disabled))), ({ fullWidth }) => fullWidth && { position: 'relative' }, ({ variant, theme }) => variant && buttonVariant(variant)(theme), ({ size, variant }) => size && buttonSize(size)(variant), ({ oops, theme: { color } }) => oops && [
    oopsAnimation,
    Object.assign(Object.assign({ transform: 'translate3d(0, 0, 0)', backfaceVisibility: 'hidden', perspective: rem(1000), color: color.goten[100], backgroundColor: color.chiChi[100] }, buttonHover({
        backgroundColor: color.chiChi[100],
    })), buttonActive({
        backgroundColor: color.chiChi[100],
    })),
], ({ pulse, theme: { color } }) => pulse && [
    {
        boxShadow: `0 0 0 0 ${color.piccolo[100]}`,
    },
    pulseAnimation,
], ({ iconLeft, size }) => [
    iconLeft && size === 'xsmall' && { paddingLeft: rem(8) },
    iconLeft && size === 'small' && { paddingLeft: rem(12) },
    iconLeft && size === 'medium' && { paddingLeft: rem(16) },
    iconLeft && size === 'large' && { paddingLeft: rem(16) },
], ({ iconRight, size }) => [
    iconRight && size === 'xsmall' && { paddingRight: rem(8) },
    iconRight && size === 'small' && { paddingRight: rem(12) },
    iconRight && size === 'medium' && { paddingRight: rem(16) },
    iconRight && size === 'large' && { paddingRight: rem(16) },
], ({ isIcon, size }) => [
    isIcon &&
        size === 'xsmall' && {
        paddingLeft: rem(3),
        paddingRight: rem(3),
        height: rem(32),
    },
    isIcon &&
        size === 'small' && {
        paddingLeft: rem(3),
        paddingRight: rem(3),
        height: rem(40),
    },
    isIcon &&
        size === 'medium' && {
        paddingLeft: rem(3),
        paddingRight: rem(3),
        height: rem(48),
    },
    isIcon &&
        size === 'large' && {
        paddingLeft: rem(3),
        paddingRight: rem(3),
        height: rem(56),
    },
]);
const InnerContainer = styled.div({
    display: 'grid',
    height: '100%',
    gridAutoFlow: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: rem(8),
});
const IconLeftWrapper = styled.span(({ size, fullWidth }) => [
    size === 'xsmall' && {
        fontSize: rem(16),
        lineHeight: 1,
    },
    size === 'small' && {
        fontSize: rem(16),
        lineHeight: 1,
    },
    size === 'medium' && {
        fontSize: rem(20),
        lineHeight: 1,
    },
    size === 'large' && {
        fontSize: rem(24),
        lineHeight: 1,
    },
    fullWidth &&
        size === 'xsmall' && {
        position: 'absolute',
        left: rem(8),
        top: rem(8),
    },
    fullWidth &&
        size === 'small' && {
        position: 'absolute',
        left: rem(12),
        top: rem(10),
    },
    fullWidth &&
        size === 'medium' && {
        position: 'absolute',
        left: rem(16),
        top: rem(12),
    },
    fullWidth &&
        size === 'large' && {
        position: 'absolute',
        left: rem(16),
        top: rem(12),
    },
]);
const IconRightWrapper = styled.span(({ size, fullWidth }) => [
    size === 'xsmall' && {
        fontSize: rem(16),
    },
    size === 'small' && {
        fontSize: rem(16),
    },
    size === 'medium' && {
        fontSize: rem(20),
        lineHeight: 1,
    },
    size === 'large' && {
        fontSize: rem(24),
        lineHeight: 1,
    },
    fullWidth &&
        size === 'xsmall' && {
        position: 'absolute',
        right: rem(8),
        top: rem(8),
    },
    fullWidth &&
        size === 'small' && {
        position: 'absolute',
        right: rem(12),
        top: rem(10),
    },
    fullWidth &&
        size === 'medium' && {
        position: 'absolute',
        right: rem(16),
        top: rem(12),
    },
    fullWidth &&
        size === 'large' && {
        position: 'absolute',
        right: rem(16),
        top: rem(12),
    },
]);
const Button = forwardRef((props, ref) => {
    const { children, mockState, progress, oops, success, iconLeft, iconRight, size, fullWidth } = props, buttonProps = __rest(props, ["children", "mockState", "progress", "oops", "success", "iconLeft", "iconRight", "size", "fullWidth"]);
    const { color, space } = useTheme();
    let content = children;
    if (progress) {
        content = (React.createElement("div", { style: { position: 'relative' } },
            React.createElement("div", { style: { visibility: 'hidden' } }, children),
            React.createElement("div", { style: {
                    position: 'absolute',
                    left: `calc(50% - ${rem(space.small)})`,
                    top: 0,
                    height: rem(space.default),
                } },
                React.createElement(Loader, { color: color.goten[100] }))));
    }
    if (success) {
        content = (React.createElement("div", { style: { position: 'relative' } },
            React.createElement("div", { style: { visibility: 'hidden' } }, children),
            React.createElement("div", { style: {
                    position: 'absolute',
                    left: `calc(50% - ${rem(space.small)})`,
                    top: 0,
                    height: rem(space.default),
                } },
                React.createElement(IconSuccess, null))));
    }
    return (React.createElement(StyledButton, Object.assign({ ref: ref, oops: oops, className: mockState && buttonMockStateClass(mockState), size: size, iconLeft: iconLeft, iconRight: iconRight, fullWidth: fullWidth }, buttonProps),
        React.createElement(InnerContainer, null,
            iconLeft && (React.createElement(IconLeftWrapper, { size: size, fullWidth: fullWidth }, iconLeft)),
            content,
            iconRight && (React.createElement(IconRightWrapper, { size: size, fullWidth: fullWidth }, iconRight)))));
});
Button.defaultProps = {
    variant: 'default',
    size: 'small',
};
export default Button;
